import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroBanner from './components/HeroBanner';
import MovieCard from './components/MovieCard';
import SectionHeader from './components/SectionHeader';
import { getHotMovies, getNewMovies, getMoviesByCategory } from '@/mocks/movies';

const categories = [
  { key: 'movie', label: '电影', icon: 'ri-movie-line', color: 'bg-primary-500/10 text-primary-400 group-hover:bg-primary-500 group-hover:text-background-50' },
  { key: 'tv', label: '电视剧', icon: 'ri-tv-line', color: 'bg-accent-500/10 text-accent-400 group-hover:bg-accent-500 group-hover:text-background-50' },
  { key: 'variety', label: '综艺', icon: 'ri-music-line', color: 'bg-secondary-500/10 text-secondary-400 group-hover:bg-secondary-500 group-hover:text-background-50' },
  { key: 'anime', label: '动漫', icon: 'ri-rocket-line', color: 'bg-primary-500/10 text-primary-400 group-hover:bg-primary-500 group-hover:text-background-50' },
];

export default function Home() {
  const hotMovies = getHotMovies();
  const newMovies = getNewMovies();
  const tvShows = getMoviesByCategory('tv').slice(0, 6);
  const varietyShows = getMoviesByCategory('variety').slice(0, 6);
  const animeShows = getMoviesByCategory('anime').slice(0, 6);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        {/* Hero Banner */}
        <HeroBanner />

        {/* Category Quick Nav */}
        <section className="relative z-10 -mt-10 md:-mt-12 px-4 md:px-6" aria-label="内容分类">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.key}
                  to={`/category/${cat.key}`}
                  className={`group flex items-center gap-3 px-4 py-4 md:px-5 md:py-5 rounded-xl bg-background-100 border border-background-200/50 hover:border-background-300/50 transition-all duration-300 cursor-pointer`}
                >
                  <div className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg transition-all duration-300 ${cat.color}`}>
                    <i className={`${cat.icon} text-lg md:text-xl`}></i>
                  </div>
                  <div>
                    <span className="text-sm md:text-base font-semibold text-foreground-50">{cat.label}</span>
                    <p className="text-xs text-foreground-500 mt-0.5">海量资源</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Movies */}
        <section className="mt-10 md:mt-14 px-4 md:px-6" aria-label="热门推荐">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="热门推荐"
              subtitle="全网热播，好评如潮"
              linkTo="/category/movie"
              linkLabel="更多电影"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {hotMovies.slice(0, 6).map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* New Arrivals */}
        <section className="mt-10 md:mt-14 px-4 md:px-6" aria-label="最新上线">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="最新上线"
              subtitle="新鲜出炉，抢先观看"
              linkTo="/category/all"
              linkLabel="更多新片"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {newMovies.slice(0, 6).map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* TV Series */}
        <section className="mt-10 md:mt-14 px-4 md:px-6" aria-label="热门电视剧">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="热门电视剧"
              subtitle="精彩剧集，一追到底"
              linkTo="/category/tv"
              linkLabel="更多剧集"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {tvShows.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Variety Shows */}
        <section className="mt-10 md:mt-14 px-4 md:px-6" aria-label="热门综艺">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="热门综艺"
              subtitle="欢乐不停，笑声不断"
              linkTo="/category/variety"
              linkLabel="更多综艺"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {varietyShows.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Anime */}
        <section className="mt-10 md:mt-14 px-4 md:px-6" aria-label="热门动漫">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="热门动漫"
              subtitle="二次元世界，无限精彩"
              linkTo="/category/anime"
              linkLabel="更多动漫"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {animeShows.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="mt-14 md:mt-20 px-4 md:px-6" aria-label="观影邀请">
          <div className="max-w-7xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-primary-500/20 via-accent-500/10 to-primary-500/20 border border-primary-500/10">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary-500/30 via-transparent to-transparent"></div>
              </div>
              <div className="relative px-6 py-10 md:px-12 md:py-14 text-center">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-50 mb-3">
                  春夜观影，畅享高清视界
                </h2>
                <p className="text-sm md:text-base text-foreground-400 mb-6 max-w-md mx-auto">
                  海量影视资源 · 蓝光画质 · 流畅播放 · 完全免费
                </p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <Link
                    to="/category/movie"
                    className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-400 transition-all duration-300 whitespace-nowrap"
                  >
                    开始观影
                  </Link>
                  <Link
                    to="/about"
                    className="px-6 py-2.5 rounded-full bg-foreground-50/10 text-foreground-50 font-medium text-sm hover:bg-foreground-50/20 transition-all duration-300 whitespace-nowrap"
                  >
                    了解更多
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Schema.org Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "春夜影院",
        "url": "https://www.chunyecinema.com",
        "description": "春夜影院提供最新电影全集与电视剧大全，免费高清在线观看，不缓冲流畅播放。",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://www.chunyecinema.com/search?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "春夜影院 - 首页",
        "url": "https://www.chunyecinema.com/",
        "description": "春夜影院首页，提供最新电影、电视剧、综艺、动漫免费高清在线观看。",
        "isPartOf": { "@type": "WebSite", "name": "春夜影院", "url": "https://www.chunyecinema.com" }
      }) }} />
    </div>
  );
}